'use client';
import * as React from 'react';
import { CheckRounded, LockRounded, PlayArrow } from '@mui/icons-material';
import { Button, Chip, Typography, LinearProgress } from '@mui/material';
import { Instructor, ModalType } from '../../types/modals';
import { formatDuration } from '../../utils/return-duration-from-seconds';
import { SeriesByIdEventKeys, SeriesByIdEvents } from '../../events';
import { EventBus } from '@/shared/utils';
import { routes } from 'src/shared/utils/routes';

interface DetailEduPathProps {
  instructors: Instructor[];
}

interface LessonItemProps extends DetailEduPathProps {
  title: string;
  duration: number;
  documentId: string;
  seriesDocumentId: string;
  serieName: string;
  playbackId: string | null;
  description: string;
  eventBus: EventBus<SeriesByIdEvents>;
  lessonProgressesDocumentId: string;
  lessonProgress: number;
  onClickCallback: (documentId: string) => void;
  openModalCallback: (type: 'video-modal' | 'lesson-completion-modal', data: ModalType) => void;
  isSubscriber: boolean;
  isWatched?: boolean;
  isLocked?: boolean;
  isJukebox?: boolean;
  imageSrc?: string;
  isCurrent?: boolean;
}

const LessonItem: React.FC<LessonItemProps> = ({
  title,
  documentId,
  duration,
  description,
  eventBus,
  lessonProgressesDocumentId,
  seriesDocumentId,
  serieName,
  isWatched = false,
  lessonProgress = 0,
  isSubscriber,
  isLocked,
  isJukebox,
  isCurrent,
  imageSrc,
  instructors = [],
  playbackId,
  onClickCallback,
  openModalCallback,
}) => {
  const returnButtonLabel = (isWatched: boolean, lessonProgress: number) => {
    if (isWatched) return 'Guarda Nuovamente';
    if (lessonProgress > 0) return 'Continua a guardare';
    return 'Guarda lezione';
  };
  const formattedDuration = formatDuration(duration ?? 0);
  const handleCallbackModal = (type: 'video-modal' | 'lesson-completion-modal') => {
    if (!openModalCallback) return;
    openModalCallback(type, {
      documentId,
      title,
      playbackId,
      description,
      instructors,
      duration,
      imageSrc,
      isWatched,
      lessonDocumentId: documentId,
      lessonProgress: {
        documentId: lessonProgressesDocumentId,
        current_second: lessonProgress,
        progress_status: isWatched ? 'completed' : 'in_progress',
      },
    });
  };
  return (
    <article
      className="flex gap-x-4 gap-y-2"
      onClick={() => {
        if (isLocked) return;
        onClickCallback(documentId);
      }}
    >
      <div className="relative rounded-xl bg-paper overflow-hidden w-full">
        <div
          className={`h-full w-[8px] absolute ${
            isWatched || isCurrent ? 'bg-primary-dark' : 'bg-neutral-main'
          } ${isLocked && 'bg-paper'}`}
        />
        <div className="flex flex-col justify-start md:flex-row md:items-center w-full gap-4 p-4 pl-6">
          {isCurrent && (
            <div className="w-full md:w-auto">
              <div className="flex overflow-hidden rounded-md">
                <img
                  loading="eager"
                  src={imageSrc}
                  alt=""
                  className="object-contain aspect-[1.68] w-[100px] md:w-[150px]"
                />
              </div>
            </div>
          )}
          <div className="flex flex-row md:flex-col flex-1 gap-2">
            <Typography
              variant="h5"
              className={`w-full ${
                isWatched || isLocked || isJukebox ? 'text-text-disabled' : 'text-primary'
              }`}
            >
              {title}
            </Typography>
            <Chip
              label={<Typography variant="button-s">{formattedDuration}</Typography>}
              size="medium"
              variant="filled"
              className="self-start bg-neutral-main"
            />
          </div>

          {!isLocked && !isCurrent && !isWatched && lessonProgress > 0 && (
            <LinearProgress
              variant="determinate"
              value={lessonProgress}
              className="w-[120px] h-[10px]"
              sx={{ backgroundColor: '#E2E3E9' }}
            />
          )}

          {!isCurrent && isWatched && (
            <div className="flex lg:flex-col md:justify-center items-center justify-end gap-1 text-text-disabled">
              <CheckRounded />
              <Typography variant="caption-500">Guardato</Typography>
            </div>
          )}

          {isLocked && (
            <div className="flex md:flex-col md:justify-center items-center justify-end text-text-disabled">
              <LockRounded className="h-[48px]" />
              <Typography variant="caption-500">Abbonati per guardare</Typography>
            </div>
          )}

          {isCurrent && (
            <div className="flex flex-col my-4">
              {isSubscriber && (
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  className="mb-4"
                  onClick={() => {
                    handleCallbackModal('lesson-completion-modal');
                  }}
                >
                  <Typography variant="button-s">
                    {isWatched ? 'Segna come da vedere' : 'Segna come completata'}
                  </Typography>
                </Button>
              )}
              <Button
                size="small"
                variant="contained"
                color="primary"
                className="w-full gap-x-2"
                onClick={() => {
                  if (isSubscriber) {
                    eventBus.emit(SeriesByIdEventKeys.redirects.to, {
                      url: routes.serieLesson.makeUrl(
                        seriesDocumentId,
                        serieName,
                        title,
                        documentId,
                      ),
                      isExternalPage: false,
                    });
                    return;
                  }
                  handleCallbackModal('video-modal');
                }}
              >
                <PlayArrow className="size-4" />
                <Typography variant="button-s">
                  {returnButtonLabel(isWatched, lessonProgress)}
                </Typography>
              </Button>
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

export default LessonItem;
